import { EMPTY_STRING } from '@rotomeca/utils';
import { HTMLBnumCardItem } from '../card-item/bnum-card-item';
import { HTMLBnumDate } from '../../atoms/date/bnum-date';
import style from './bnum-card-item-mail.less';
import JsEvent from '@rotomeca/event';
import { Nullable } from '@rotomeca/utils';
import { CallbackChangedSimple } from '../../../core/utils/types';
import { EVENT_DEFAULT } from '../../../core/utils/constants/constants';
import { TAG_CARD_ITEM_MAIL } from '../../../core/utils/constants/tags';
import { Scheduler } from '../../../core/utils/Scheduler';
import { Define, Self } from '../../../core/decorators';
import { CustomDom } from '../../bnum-element';
import { BnumDateUtils } from '../../../core/utils/BnumDateUtils';
import {
	NO_DEFAULT,
	render,
} from '../card-item/bnum-card-item.internal/template';
import { HTMLBnumFragment } from 'components/atoms';
import { Listener } from 'core/decorators/listener';
import {
	OnDateChangedInitializer,
	OnSenderChangedInitializer,
	OnSubjectChangedInitializer,
} from './bnum-card-item-mail.internal/listeners';
import { UI } from 'core/decorators/ui';
import {
	ATTRIBUTE_DATA_DATE,
	ATTRIBUTE_DATA_SENDER,
	ATTRIBUTE_DATA_SUBJECT,
	ATTRIBUTE_READ,
	CLASS_DATE,
	CLASS_MAIN_CONTENT,
	CLASS_SENDER,
	CLASS_SUBJECT,
	DATA_DATE,
	DATA_SENDER,
	DATA_SUBJECT,
	EVENT_DATE_CHANGED,
	EVENT_SENDER_CHANGED,
	EVENT_SUBJECT_CHANGED,
	ID_DATE_ELEMENT_OVERRIDE,
	ID_DATE_SLOT,
	ID_SENDER_SLOT,
	ID_SUBJECT_SLOT,
	PART_DATE_OVERRIDE,
	PART_SENDER_OVERRIDE,
	PART_SUBJECT_OVERRIDE,
	SLOT_DATE_NAME,
	SLOT_SENDER_NAME,
	SLOT_SUBJECT_NAME,
	STATE_READ,
} from './bnum-card-item-mail.internal/constants';
import { UpdateAll } from 'core/decorators/updateAll';

//#region Types
type ChangedEvent = JsEvent<CallbackChangedSimple<HTMLBnumCardItemMail>>;
type HTMLBnumCardItemMailUi = Readonly<{
	slotSender: HTMLSlotElement;
	slotDate: HTMLSlotElement;
	slotSubject: HTMLSlotElement;
	overriderSender: HTMLSlotElement;
	overriderSubject: HTMLSlotElement;
	overriderDate: HTMLSlotElement;
}>;
//#endregion Types

//#region Global Constants
const SHEET = HTMLBnumCardItem.ConstructCSSStyleSheet(style);
const EVENTS = {
	CHANGED: {
		SENDER: EVENT_SENDER_CHANGED,
		SUBJECT: EVENT_SUBJECT_CHANGED,
		DATE: EVENT_DATE_CHANGED,
	},
} as const;

/**
 * Format d'affichage de la date pour aujourd'hui.
 */
const TODAY_FORMAT = 'HH:mm';
/**
 * Format d'affichage de la date pour les autres jours.
 */
const OTHER_DAY_FORMAT = 'dd/MM/yyyy';
/**
 * Format d'affichage de la date pour la semaine.
 */
const WEEK_FORMAT = 'E - HH:mm';
const SYMBOL_RESET = Symbol('reset');
//#endregion Global Constants

//#region Template
const TEMPLATE = (
	<HTMLBnumFragment>
		<div class={CLASS_MAIN_CONTENT} part={CLASS_MAIN_CONTENT}>
			<div class={CLASS_SENDER} part={CLASS_SENDER}>
				<slot id={ID_SENDER_SLOT} name={SLOT_SENDER_NAME}></slot>
				<span
					class={PART_SENDER_OVERRIDE}
					part={PART_SENDER_OVERRIDE}
					hidden
				></span>
			</div>
			<div class={CLASS_SUBJECT} part={CLASS_SUBJECT}>
				<slot id={ID_SUBJECT_SLOT} name={SLOT_SUBJECT_NAME}></slot>
				<span
					class={PART_SUBJECT_OVERRIDE}
					part={PART_SUBJECT_OVERRIDE}
					hidden
				></span>
			</div>
		</div>
		<div class={CLASS_DATE}>
			<slot id={ID_DATE_SLOT} name={SLOT_DATE_NAME}></slot>
			<span class={PART_DATE_OVERRIDE} part={PART_DATE_OVERRIDE} hidden>
				<HTMLBnumDate id={ID_DATE_ELEMENT_OVERRIDE}></HTMLBnumDate>
			</span>
		</div>
	</HTMLBnumFragment>
);
//#endregion Template

/**
 * Composant HTML personnalisé représentant un élément de carte mail.
 *
 * Permet d'afficher un sujet, un expéditeur et une date, avec possibilité d'override du contenu par défaut.
 *
 * Utilise des slots pour l'intégration dans le Shadow DOM et propose des méthodes pour forcer ou réinitialiser le contenu.
 *
 * Note: En passant par `data-date` ou `.updateDate()`, le format d'affichage de la date est ajusté selon la logique métier :
 * - Si la date est aujourd'hui, seule l'heure est affichée (HH:mm).
 * - Si la date est comprise entre hier et il y a 7 jours, le jour de la semaine et l'heure sont affichés (E - HH:mm).
 * - Sinon, le format par défaut de HTMLBnumDate est utilisé.
 *
 * @category Card
 *
 * @structure Item de carte mail
 * <bnum-card-item-mail data-date="now">
 * <span slot="subject">Sujet par défaut</span>
 * <span slot="sender">Expéditeur par défaut</span>
 * </bnum-card-item-mail>
 *
 * @structure Item de carte data
 * <bnum-card-item-mail data-date="2025-10-31 11:11" data-subject="Sujet ici" data-sender="Expéditeur ici">
 * </bnum-card-item-mail>
 *
 * @structure Item de carte lue
 * <bnum-card-item-mail read data-date="2025-10-31 11:11" data-subject="Sujet ici" data-sender="Expéditeur ici">
 * </bnum-card-item-mail>
 *
 * @state read - Actif quand le mail est marqué comme lu.
 *
 * @attr {string} (optional) data-subject - Sujet du mail.
 * @attr {string} (optional) data-sender - Expéditeur du mail.
 * @attr {string} (optional) data-date - Date du mail, optionnel, mais conseillé si vous voulez la logique de formatage automatique.
 * @attr {boolean} (optional) read - Indique si le mail est lu.
 *
 * @event {CustomElement<{ caller: HTMLBnumCardItemMail }>} bnum-card-item-mail:sender-changed - Événement déclenché lors du changement de l'expéditeur du mail.
 * @event {CustomElement<{ caller: HTMLBnumCardItemMail }>} bnum-card-item-mail:subject-changed - Événement déclenché lors du changement du sujet du mail.
 * @event {CustomElement<{ caller: HTMLBnumCardItemMail }>} bnum-card-item-mail:date-changed - Événement déclenché lors du changement de la date du mail.
 *
 * @slot (default) - N'existe pas, si vous mettez du contenu en dehors des slots, ils ne seront pas affichés.
 * @slot sender - Contenu de l'expéditeur (texte ou HTML).
 * @slot subject - Contenu du sujet (texte ou HTML).
 * @slot date - Contenu de la date. /!\ Si vous passez par ce slot, la mécanique de formatage automatique de la date ne s'appliquera pas.
 */
@Define({ tag: TAG_CARD_ITEM_MAIL, template: render(TEMPLATE, NO_DEFAULT) })
@UpdateAll()
export class HTMLBnumCardItemMail extends HTMLBnumCardItem {
	//#region Private fields
	/**
	 * Élément HTMLBnumDate utilisé pour override la date.
	 */
	#_dateOverrideElement: HTMLBnumDate | null = null;
	/**
	 * Scheduler pour la mise à jour du sujet.
	 */
	#_subjectScheduler: Nullable<Scheduler<string | HTMLElement | symbol>> = null;
	/**
	 * Scheduler pour la mise à jour de la date.
	 */
	#_dateScheduler: Nullable<Scheduler<string | Date | HTMLBnumDate | symbol>> =
		null;
	#_defaultDate: HTMLBnumDate | null = null;
	/**
	 * Scheduler pour la mise à jour de l'expéditeur.
	 */
	#_senderScheduler: Nullable<Scheduler<string | HTMLElement | symbol>> = null;
	//#endregion Private fields

	//#region Getters
	@UI({
		slotSender: `#${ID_SENDER_SLOT}`,
		slotDate: `#${ID_DATE_SLOT}`,
		slotSubject: `#${ID_SUBJECT_SLOT}`,
		overriderSender: `.${PART_SENDER_OVERRIDE}`,
		overriderSubject: `.${PART_SUBJECT_OVERRIDE}`,
		overriderDate: `.${PART_DATE_OVERRIDE}`,
	})
	accessor #_ui!: HTMLBnumCardItemMailUi;

	/** Référence à la classe HTMLBnumCardItemMail */
	@Self _!: typeof HTMLBnumCardItemMail;

	/**
	 * Événement déclenché lors du changement du sujet du mail.
	 * Permet d'attacher des gestionnaires personnalisés au changement de sujet.
	 */
	@Listener(OnSubjectChangedInitializer)
	accessor onsubjectchanged!: ChangedEvent;

	/**
	 * Événement déclenché lors du changement de l'expéditeur du mail.
	 * Permet d'attacher des gestionnaires personnalisés au changement d'expéditeur.
	 */
	@Listener(OnSenderChangedInitializer) accessor onsenderchanged!: ChangedEvent;

	/**
	 * Événement déclenché lors du changement de la date du mail.
	 * Permet d'attacher des gestionnaires personnalisés au changement de date.
	 */
	@Listener(OnDateChangedInitializer) accessor ondatechanged!: ChangedEvent;

	/**
	 * Retourne l'élément HTMLBnumDate pour l'override de la date.
	 *
	 * Initialise la variable si elle n'a pas encore été initialisée.
	 */
	get #_lazyDateOverrideElement(): HTMLBnumDate {
		return (this.#_dateOverrideElement ??= (() => {
			const existingDateElement = this.#_queryById<HTMLBnumDate>(
				this.#_ui.overriderDate,
				ID_DATE_ELEMENT_OVERRIDE,
			);

			this.#_configureDateElement(existingDateElement);
			return existingDateElement;
		})());
	}

	// --- Getters pour lire les data-attributs ---
	/**
	 * Retourne la date du mail, en tenant compte de l'override si présent.
	 */
	get date(): Date {
		return this.#_ui.overriderDate?.hidden === false
			? this.#_lazyDateOverrideElement.getDate()!
			: (this.#_defaultDate?.getDate?.() ?? new Date());
	}
	/**
	 * Retourne le sujet du mail depuis l'attribut data.
	 */
	get #_mailSubject(): string {
		return this.data(DATA_SUBJECT) || EMPTY_STRING;
	}
	/**
	 * Retourne la date du mail depuis l'attribut data.
	 */
	get #_mailDate(): string {
		return this.data(DATA_DATE) || EMPTY_STRING;
	}
	/**
	 * Retourne l'expéditeur du mail depuis l'attribut data.
	 */
	get #_mailSender(): string {
		return this.data(DATA_SENDER) || EMPTY_STRING;
	}
	//#endregion Getters

	//#region Lifecycle
	/**
	 * Constructeur du composant.
	 */
	constructor() {
		super();
	}

	/**
	 * Crée le layout du Shadow DOM (avec slots ET overrides).
	 * @param container Le conteneur du Shadow DOM ou un élément HTML.
	 */
	protected _p_buildDOM(container: CustomDom): void {
		super._p_buildDOM(container);

		// On écrase _p_slot car dans notre template, il n'y a pas de slot par défaut
		this._p_slot = container.queryId(ID_SUBJECT_SLOT);
	}

	/**
	 * Crée le contenu par défaut et l'attache aux slots.
	 * Initialise les nœuds pour le sujedate-element-overridet, l'expéditeur et la date.
	 */
	protected _p_attach(): void {
		super._p_attach();

		if (this.#_mailSubject !== EMPTY_STRING)
			this._p_slot!.appendChild(this._p_createTextNode(this.#_mailSubject));

		// Crée le nœud texte pour l'EXPÉDITEUR par défaut
		if (this.#_mailSender !== EMPTY_STRING)
			this.#_ui.slotSender!.appendChild(
				this._p_createTextNode(this.#_mailSender),
			);

		if (this.#_mailDate !== EMPTY_STRING) {
			// Crée l'élément DATE par défaut
			const defaultDate = HTMLBnumDate.Create(this.#_mailDate);
			this.#_configureDateElement(defaultDate); // Applique la logique
			this.#_ui.slotDate!.appendChild(defaultDate);
			this.#_defaultDate = defaultDate;
		}
	}

	/**
	 * Retourne les stylesheets à appliquer au composant.
	 * @returns Liste des CSSStyleSheet à appliquer.
	 */
	protected _p_getStylesheets(): CSSStyleSheet[] {
		return [...super._p_getStylesheets(), SHEET];
	}

	/**
	 * Méthode appelée lors de la mise à jour d'un attribut observé.
	 * @param name Nom de l'attribut.
	 * @param oldVal Ancienne valeur.
	 * @param newVal Nouvelle valeur.
	 */
	protected _p_update(): void {
		super._p_update();

		if (this.hasAttribute(ATTRIBUTE_READ)) this._p_addState(STATE_READ);
	}
	//#endregion Lifecycle

	//#region Public methods
	/**
	 * Force le contenu de l'expéditeur, en ignorant le slot.
	 * @param content Contenu texte ou HTML à afficher comme expéditeur.
	 * @returns L'instance courante pour chaînage.
	 */
	updateSender(content: string | HTMLElement): this {
		return this.#_requestUpdateSender(content);
	}

	/**
	 * Réaffiche le contenu du slot "sender" (annule l'override).
	 * @returns L'instance courante pour chaînage.
	 */
	resetSender(): this {
		return this.#_requestUpdateSender(SYMBOL_RESET);
	}

	/**
	 * Force le contenu du sujet, en ignorant le slot.
	 * @param content Contenu texte ou HTML à afficher comme sujet.
	 * @returns L'instance courante pour chaînage.
	 */
	updateSubject(content: string | HTMLElement): this {
		return this.#_requestUpdateSubject(content);
	}

	/**
	 * Réaffiche le contenu du slot "subject" (annule l'override).
	 * @returns L'instance courante pour chaînage.
	 */
	resetSubject(): this {
		return this.#_requestUpdateSubject(SYMBOL_RESET);
	}

	/**
	 * Force le contenu de la date, en ignorant le slot.
	 * @param content Chaîne, Date ou élément HTML à afficher comme date.
	 * @returns L'instance courante pour chaînage.
	 */
	updateDate(content: string | Date | HTMLBnumDate): this {
		return this.#_requestUpdateDate(content);
	}

	/**
	 * Réaffiche le contenu du slot "date" (annule l'override).
	 * @returns L'instance courante pour chaînage.
	 */
	resetDate(): this {
		return this.#_requestUpdateDate(SYMBOL_RESET);
	}
	//#endregion Public methods
	//#region Private methods
	/**
	 * Met à jour l'affichage de l'expéditeur (slot ou override).
	 * @param content Contenu à afficher ou symbole de reset.
	 */
	#_updateSender(content: string | HTMLElement | symbol): void {
		if (!this.#_ui.overriderSender || !this.#_ui.slotSender) return;

		if (content === SYMBOL_RESET) {
			this.#_ui.slotSender.hidden = false;
			this.#_ui.overriderSender.hidden = true;
		} else {
			if (typeof content === 'string')
				this.#_ui.overriderSender.innerHTML = content;
			else this.#_ui.overriderSender.replaceChildren(content as HTMLElement);

			// On cache le slot, on montre l'override
			this.#_ui.slotSender.hidden = true;
			this.#_ui.overriderSender.hidden = false;
		}

		this.onsenderchanged.call(this);
	}

	/**
	 * Planifie la mise à jour de l'expéditeur.
	 * @param content Contenu à afficher ou symbole de reset.
	 * @returns L'instance courante pour chaînage.
	 */
	#_requestUpdateSender(content: string | HTMLElement | symbol): this {
		(this.#_senderScheduler ??= new Scheduler<string | HTMLElement | symbol>(
			value => this.#_updateSender(value!),
		)).schedule(content);

		return this;
	}

	/**
	 * Met à jour l'affichage du sujet (slot ou override).
	 * @param content Contenu à afficher ou symbole de reset.
	 */
	#_updateSubject(content: string | HTMLElement | symbol): void {
		if (!this.#_ui.overriderSubject || !this.#_ui.slotSubject) return;

		if (content === SYMBOL_RESET) {
			this.#_ui.slotSubject.hidden = false;
			this.#_ui.overriderSubject.hidden = true;
		} else if (typeof content === 'string')
			this.#_ui.overriderSubject.innerHTML = content;
		else this.#_ui.overriderSubject.replaceChildren(content as HTMLElement);

		// On cache le slot, on montre l'override
		this.#_ui.slotSubject.hidden = true;
		this.#_ui.overriderSubject.hidden = false;

		this.onsubjectchanged.call(this);
	}

	/**
	 * Planifie la mise à jour du sujet.
	 * @param content Contenu à afficher ou symbole de reset.
	 * @returns L'instance courante pour chaînage.
	 */
	#_requestUpdateSubject(content: string | HTMLElement | symbol): this {
		(this.#_subjectScheduler ??= new Scheduler<string | HTMLElement | symbol>(
			value => this.#_updateSubject(value!),
		)).schedule(content);

		return this;
	}

	/**
	 * Met à jour l'affichage de la date (slot ou override).
	 * @param content Contenu à afficher ou symbole de reset.
	 */
	#_updateDate(content: string | Date | HTMLBnumDate | symbol): void {
		if (!this.#_ui.overriderDate || !this.#_ui.slotDate) return;

		if (content === SYMBOL_RESET) {
			this.#_ui.slotDate.hidden = false;
			this.#_ui.overriderDate.hidden = true;
		} else {
			if (typeof content === 'string' || content instanceof Date)
				this.#_lazyDateOverrideElement.setDate(content);
			else
				this.#_lazyDateOverrideElement.setDate(
					(content as HTMLBnumDate).getDate()!,
				);

			this.#_ui.slotDate.hidden = true;
			this.#_ui.overriderDate.hidden = false;
		}

		this.ondatechanged.call(this);
	}

	/**
	 * Planifie la mise à jour de la date.
	 * @param content Contenu à afficher ou symbole de reset.
	 * @returns L'instance courante pour chaînage.
	 */
	#_requestUpdateDate(content: string | Date | HTMLBnumDate | symbol): this {
		(this.#_dateScheduler ??= new Scheduler<
			string | Date | HTMLBnumDate | symbol
		>(value => this.#_updateDate(value!))).schedule(content);

		return this;
	}

	/**
	 * Recherche un élément par son id dans le container donné.
	 * @param container Container dans lequel chercher.
	 * @param id Id de l'élément.
	 * @returns L'élément trouvé.
	 */
	#_queryById<T extends HTMLElement>(
		container: ShadowRoot | HTMLElement,
		id: string,
	): T {
		return container instanceof ShadowRoot
			? (container.getElementById(id) as T)
			: (container.querySelector(`#${id}`) as T);
	}

	/**
	 * Configure le format d'affichage de la date selon la logique métier :
	 * - Affiche l'heure si la date est aujourd'hui.
	 * - Affiche le jour et l'heure si la date est comprise entre hier et il y a 7 jours.
	 * - Sinon, conserve le format par défaut.
	 * @param element Instance de HTMLBnumDate à configurer.
	 */
	#_configureDateElement(element: HTMLBnumDate): void {
		this._.SetDateLogique(element);
	}

	//#endregion Private methods

	//#region Static methods
	/**
	 * Applique la logique de formatage de date à un élément HTMLBnumDate.
	 * @param element Élément HTMLBnumDate à configurer.
	 */
	static SetDateLogique(element: HTMLBnumDate): void {
		element.formatEvent.add(EVENT_DEFAULT, param => {
			const originalDate = element.getDate();
			if (!originalDate) return param;
			if (BnumDateUtils.isToday(originalDate)) {
				return {
					date: BnumDateUtils.format(
						originalDate,
						BnumDateUtils.getOptionsFromToken(TODAY_FORMAT),
						element.localeElement,
					),
				};
			}
			const now = new Date();
			const startOfInterval = BnumDateUtils.startOfDay(
				BnumDateUtils.subDays(now, 7),
			);
			const endOfInterval = BnumDateUtils.endOfDay(
				BnumDateUtils.subDays(now, 1),
			);
			if (
				BnumDateUtils.isWithinInterval(originalDate, {
					start: startOfInterval,
					end: endOfInterval,
				})
			) {
				return {
					date: BnumDateUtils.format(
						originalDate,
						BnumDateUtils.getOptionsFromToken(WEEK_FORMAT),
						element.localeElement,
					),
				};
			}
			return {
				date: BnumDateUtils.format(
					originalDate,
					BnumDateUtils.getOptionsFromToken(OTHER_DAY_FORMAT),
					element.localeElement,
				), // Format par défaut si aucune condition n'est remplie
			};
		});
	}

	static _p_observedAttributes(): string[] {
		return [...super._p_observedAttributes(), ATTRIBUTE_READ];
	}

	/**
	 * Crée une nouvelle instance du composant avec les valeurs fournies.
	 * @param subject Sujet du mail.
	 * @param sender Expéditeur du mail.
	 * @param date Date du mail
	 * @returns Instance HTMLBnumCardItemMail.
	 */
	static Create(
		subject: string,
		sender: string,
		date: string | Date,
	): HTMLBnumCardItemMail {
		const node = document.createElement(this.TAG) as HTMLBnumCardItemMail;

		node.attr(ATTRIBUTE_DATA_SUBJECT, subject);
		node.attr(ATTRIBUTE_DATA_SENDER, sender);

		if (typeof date === 'string') node.attr(ATTRIBUTE_DATA_DATE, date);
		else node.attr(ATTRIBUTE_DATA_DATE, date.toISOString());

		return node;
	}

	static get EVENTS_AVAILABLES(): Readonly<typeof EVENTS> {
		return EVENTS;
	}
	//#endregion Static methods
}
